import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  adPixelsConfig,
  isGoogleAdsActive,
  isMetaActive,
  isTikTokActive,
} from '@/lib/adPixelsConfig';
import { canLoadMarketingPixels, getMarketingConsent } from '@/lib/marketingConsent';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
    ttq?: any;
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

function loadMeta(pixelId: string) {
  if (window.fbq) {
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    return;
  }
  !(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq!('init', pixelId);
  window.fbq!('track', 'PageView');
}

function loadTikTok(pixelId: string) {
  if (window.ttq?.page) {
    window.ttq.page();
    return;
  }
  !(function (w: any, d: any, t: any) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
    ];
    ttq.setAndDefer = function (t: any, e: any) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t: any) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e: string, n?: any) {
      const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = d.createElement('script');
      o.type = 'text/javascript';
      o.async = true;
      o.src = i + '?sdkid=' + e + '&lib=' + t;
      const a = d.getElementsByTagName('script')[0];
      a.parentNode!.insertBefore(o, a);
    };
  })(window, document, 'ttq');
  window.ttq.load(pixelId);
  window.ttq.page();
}

function loadGoogleAds(adsId: string) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('config', adsId);
}

/** Mount once under BrowserRouter. Loads pixels only when flags+IDs+consent allow. */
export default function AdPixels() {
  const location = useLocation();

  useEffect(() => {
    const tryLoad = () => {
      if (!canLoadMarketingPixels(adPixelsConfig.requireConsent)) return;
      if (isMetaActive()) loadMeta(adPixelsConfig.meta.id);
      if (isTikTokActive()) loadTikTok(adPixelsConfig.tiktok.id);
      if (isGoogleAdsActive()) loadGoogleAds(adPixelsConfig.googleAds.id);
    };
    tryLoad();
    const onConsent = () => tryLoad();
    window.addEventListener('em-marketing-consent', onConsent);
    return () => window.removeEventListener('em-marketing-consent', onConsent);
  }, []);

  useEffect(() => {
    if (!canLoadMarketingPixels(adPixelsConfig.requireConsent)) return;
    if (getMarketingConsent() === 'denied') return;
    if (isMetaActive() && window.fbq) window.fbq('track', 'PageView');
    if (isTikTokActive() && window.ttq?.page) window.ttq.page();
    if (isGoogleAdsActive() && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);

  return null;
}
